import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { delAuth, postAuth, putAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { invalidatePaymentDeviceQueries } from '@/helpers/paymentDevices';

/**
 * Writes to the payment-devices endpoints. Every successful write refreshes every device, rental
 * and request query (see invalidatePaymentDeviceQueries), so a dialog never has to know which
 * screens show what it changed.
 */
const usePaymentDeviceApi = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useAuthContext();
  const token = currentUser?.token;

  const refresh = useCallback(() => invalidatePaymentDeviceQueries(queryClient), [queryClient]);

  return useMemo(() => {
    const headers = { Authorization: `Bearer ${token}` };
    return {
      post: async <T, B extends object>(url: string, body: B): Promise<T> => {
        const result = await postAuth<T, B>(url, headers, body);
        await refresh();
        return result;
      },
      put: async <T, B extends object>(url: string, body: B): Promise<T> => {
        const result = await putAuth<T, object, B>(url, headers, {}, body);
        await refresh();
        return result;
      },
      del: async (url: string): Promise<void> => {
        await delAuth<void>(url, headers);
        await refresh();
      },
    };
  }, [token, refresh]);
};

export default usePaymentDeviceApi;
